/**
 * Draft upserts run one at a time, in the order they were captured, inside
 * one ownership generation. A skipped or failed save must not hand its draft
 * id to a later generation, org, or blank draft.
 *
 * Phase-1 residual risk: two tabs or devices editing the same draft remain
 * last-write-wins. That can drop draft field edits. It does not publish, and
 * publication readiness still fails closed on its own.
 */

export interface DraftPersistScope {
  generation: number;
  orgId: string | null;
}

export interface DraftPersistWriteResult {
  id: string | null;
  adopt: boolean;
}

export interface OrderedDraftPersist<T> {
  (snapshot: T, scope?: DraftPersistScope): Promise<string | null>;
  bindOwnership(scope: DraftPersistScope, draftId: string | null): void;
  /** Wait for writes already queued by this editor, including handled failures. */
  whenIdle(): Promise<void>;
}

interface DraftPersistIdentity extends DraftPersistScope {
  draftId: string | null;
}

function isWriteResult(value: unknown): value is DraftPersistWriteResult {
  return typeof value === "object" && value !== null && "adopt" in value;
}

export function createOrderedDraftPersist<T>(
  write: (snapshot: T, knownId: string | null) => Promise<string | null | DraftPersistWriteResult>,
): OrderedDraftPersist<T> {
  let tail: Promise<void> = Promise.resolve();
  let identity: DraftPersistIdentity = { generation: 0, orgId: null, draftId: null };

  const sameScope = (scope: DraftPersistScope) =>
    identity.generation === scope.generation && identity.orgId === scope.orgId;

  const enqueue = (snapshot: T, scope?: DraftPersistScope): Promise<string | null> => {
    const jobScope: DraftPersistScope = scope ?? { generation: identity.generation, orgId: identity.orgId };
    const run = tail.then(async () => {
      const knownId = sameScope(jobScope) ? identity.draftId : null;
      const raw = await write(snapshot, knownId);
      const parsed = isWriteResult(raw) ? raw : { id: raw, adopt: true };
      if (!sameScope(jobScope) || !parsed.adopt) return null;
      if (parsed.id) identity = { ...identity, draftId: parsed.id };
      return parsed.id ?? identity.draftId;
    });
    tail = run.then(() => undefined, () => undefined);
    return run;
  };

  enqueue.bindOwnership = (scope: DraftPersistScope, draftId: string | null) => {
    identity = { generation: scope.generation, orgId: scope.orgId, draftId };
  };
  enqueue.whenIdle = () => tail;

  return enqueue;
}
